import { useMemo, useState } from "react";
import { Check, ChevronDown, ListFilter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export interface AdminEventOption {
  id: string;
  title: string;
  status?: string | null;
  start_date?: string | null;
  venue_city?: string | null;
  is_featured?: boolean | null;
  is_online?: boolean | null;
}

interface EventPickerProps {
  value: string | null | undefined;
  onValueChange: (value: string | null) => void;
  events: AdminEventOption[];
  placeholder?: string;
  className?: string;
  allowAll?: boolean;
}

export function EventPicker({
  value,
  onValueChange,
  events,
  placeholder = "Filtrar por evento",
  className,
  allowAll = true,
}: EventPickerProps) {
  const [open, setOpen] = useState(false);

  const selectedEvent = useMemo(() => events.find((event) => event.id === value), [events, value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-10 w-full justify-between rounded-xl border border-border/60 bg-white dark:bg-card text-sm shadow-none transition-all hover:bg-secondary/40 focus:ring-1 focus:ring-border/80 focus-visible:ring-1 focus-visible:ring-border/80",
            className,
          )}
        >
          <span className="truncate font-medium">
            {selectedEvent ? selectedEvent.title : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[500px] max-w-[calc(100vw-2rem)] p-1.5 shadow-2xl rounded-2xl border border-border/70 bg-white dark:bg-[#18181b]" align="end">
        <Command className="rounded-xl border-0 bg-transparent">
          <CommandInput placeholder="Buscar evento..." className="h-10 border-0 focus:ring-0 focus-visible:!ring-0 focus-visible:!ring-offset-0 focus-visible:!outline-none" />
          <CommandList className="scrollbar-thin">
            <CommandEmpty className="py-4 text-center text-xs text-muted-foreground">Nenhum evento encontrado.</CommandEmpty>
            <CommandGroup className="px-1 py-1.5">
              {allowAll && (
                <CommandItem
                  value="todos"
                  onSelect={() => {
                    onValueChange(null);
                    setOpen(false);
                  }}
                  className="group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm outline-none transition-all my-0.5 data-[selected=true]:bg-gray-300/30 data-[selected=true]:text-foreground"
                >
                  <span className="flex items-center gap-2.5 font-medium">
                    <ListFilter className="h-4 w-4 text-muted-foreground group-data-[selected=true]:text-foreground/80" />
                    Todos os eventos
                  </span>
                  {!value && <Check className="ml-auto h-4 w-4 group-data-[selected=true]:text-foreground" />}
                </CommandItem>
              )}

              {allowAll && events.length > 0 && <CommandSeparator className="my-1.5" />}

              {events.map((event) => (
                <CommandItem
                  key={event.id}
                  value={`${event.title} ${event.venue_city || ""} ${event.status || ""}`}
                  onSelect={() => {
                    onValueChange(event.id);
                    setOpen(false);
                  }}
                  className="group flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 text-sm outline-none transition-all my-0.5 data-[selected=true]:bg-gray-300/30 data-[selected=true]:text-foreground"
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold leading-none group-data-[selected=true]:text-foreground">{event.title}</span>
                      {event.is_featured ? (
                        <Badge className="h-5 border-0 bg-gray-300/20 px-1.5 text-[10px] text-foreground/70 group-data-[selected=true]:bg-gray-400/30 group-data-[selected=true]:text-foreground">Destaque</Badge>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground group-data-[selected=true]:text-foreground/80 font-medium">
                      {event.is_online ? "Online / Remoto" : (event.venue_city || "Sem cidade")}
                      {event.start_date ? ` · ${format(new Date(event.start_date), "dd MMM yyyy", { locale: ptBR })}` : ""}
                    </p>
                  </div>
                  {value === event.id && <Check className="h-4 w-4 shrink-0 mt-0.5 group-data-[selected=true]:text-foreground" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}